import { AccessGroup, CreateAccessGroupData, Role, UpdateAccessGroupData } from '@/types/iam';
import {
  useCreateAccessGroup,
  useDeleteAccessGroup,
  
  useListAccessGroups,
  useListRoles,
  useUpdateAccessGroup,
} from '@/hooks/useIAM';
import { AlertTriangle, ChevronDown, ChevronUp, Edit, Loader2, Plus, Trash, X } from 'lucide-react';
import React, { Fragment, useEffect, useState } from 'react';
import { toast } from 'sonner';

const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  groupName,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  groupName: string;
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  const handleConfirmDelete = async () => {
    setIsDeleting(true);

    const toastId = toast.loading(`Deleting access group "${groupName}"...`);

    try {
      await onConfirm();

      toast.success(`Access group "${groupName}" deleted successfully`, { id: toastId });

      onClose();
    } catch (error) {
      console.error('Error deleting access group:', error);

      toast.error(`Error deleting access group "${groupName}". Please try again.`, {
        id: toastId,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
      <div className="rounded-lg shadow-lg max-w-md w-full bg-background">
        <div className="p-5 border-b border-border flex justify-between items-center">
          <div className="flex items-center text-secondary">
            <AlertTriangle className="mr-2" size={20} />
            <h3 className="text-lg font-medium">Confirm Delete</h3>
          </div>
          <button
            onClick={onClose}
            className="hover:opacity-70 transition-opacity text-muted-foreground"
            disabled={isDeleting}
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          <p className="mb-4 text-foreground">
            Are you sure you want to delete the access group{' '}
            <span className="font-semibold">{groupName}</span>? This action cannot be undone.
          </p>

          <div className="flex justify-end space-x-3 mt-5">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-muted-foreground rounded-md transition-colors bg-background text-foreground hover:bg-muted-foreground/10"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 rounded-md transition-colors flex items-center bg-destructive text-destructive-foreground hover:opacity-90"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AccessGroupForm = ({
  accessGroup,
  roles,
  isOpen,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  accessGroup?: AccessGroup;
  roles: Role[];
  isOpen: boolean;
  onSubmit: (data: CreateAccessGroupData | UpdateAccessGroupData) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}) => {
  const [formData, setFormData] = useState({
    id: accessGroup?.id || '',
    name: accessGroup?.name || '',
    description: accessGroup?.description || '',
    priority: accessGroup?.priority || 0,
    roles: accessGroup?.roles || [],
  });

  useEffect(() => {
    setFormData({
      id: accessGroup?.id || '',
      name: accessGroup?.name || '',
      description: accessGroup?.description || '',
      priority: accessGroup?.priority || 0,
      roles: accessGroup?.roles || [],
    });
  }, [accessGroup]);

  const handleRoleToggle = (roleId: string) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter(id => id !== roleId)
        : [...prev.roles, roleId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      ...(accessGroup && { id: accessGroup.id }),
    };

    await onSubmit(submitData);
  };

  return (
    isOpen && (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
        <div className="rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto bg-background">
          <div className="p-5 border-b border-border flex justify-between items-center">
            <h3 className="text-lg font-medium text-foreground">
              {accessGroup ? 'Edit Access Group' : 'Create Access Group'}
            </h3>
            <button
              onClick={onCancel}
              className="hover:opacity-70 transition-opacity text-muted-foreground"
              disabled={isSubmitting}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5">
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Id *
              </label>
              <input
                type="text"
                required
                value={formData.id}
                disabled={!!accessGroup}
                onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-foreground">
                Priority *
              </label>
              <input
                type="number"
                required
                min="0"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-muted-foreground rounded-md focus:outline-none focus:ring-2 bg-background text-foreground"
              />
              <p className="text-xs mt-1 text-muted-foreground">
                Higher numbers indicate higher priority
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-3 text-foreground">
                Roles
              </label>
              <div className="max-h-60 overflow-y-auto border border-muted-foreground rounded-md p-3">
                {roles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No roles available. Create roles first.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {roles.map((role) => (
                      <label
                        key={role.id}
                        className="flex items-center space-x-3 cursor-pointer hover:opacity-80"
                      >
                        <input
                          type="checkbox"
                          checked={formData.roles.includes(role.id)}
                          onChange={() => handleRoleToggle(role.id)}
                          className="w-4 h-4 rounded accent-primary"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">
                            {role.name}
                          </div>
                          {role.description && (
                            <div className="text-xs text-muted-foreground">
                              {role.description}
                            </div>
                          )}
                          <div className="text-xs text-primary">
                            {role.permissions.length} permission(s)
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                Selected: {formData.roles.length} role(s)
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-muted-foreground rounded-md transition-colors bg-background text-foreground hover:bg-muted-foreground/10"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-md transition-colors flex items-center bg-primary text-primary-foreground hover:opacity-90"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {accessGroup ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  accessGroup ? 'Update' : 'Create'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );
};

const AccessGroupsTable = () => {
  const { accessGroups, fetchAccessGroups, loading } = useListAccessGroups();
  const { createAccessGroup, loading: createLoading } = useCreateAccessGroup();
  const { updateAccessGroup, loading: updateLoading } = useUpdateAccessGroup();
  const { deleteAccessGroup } = useDeleteAccessGroup();
  const { roles, fetchRoles } = useListRoles();

  const [sortField, setSortField] = useState<keyof AccessGroup>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedAccessGroup, setSelectedAccessGroup] = useState<AccessGroup | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Initialize data
  useEffect(() => {
    fetchAccessGroups();
    fetchRoles();
  }, [fetchAccessGroups, fetchRoles]);

  const showDeleteConfirmation = (id: string) => {
    setDeleteId(id);
    setIsModalOpen(true);
  };

  const handleDeleteAccessGroup = async () => {
    if (deleteId) {
      await deleteAccessGroup(deleteId);
      await fetchAccessGroups();
      setIsModalOpen(false);
      setDeleteId(null);
    }
  };

  const handleSort = (field: keyof AccessGroup) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAccessGroups = [...accessGroups].sort((a, b) => {
    if (sortField === 'priority') {
      return sortDirection === 'asc' ? a.priority - b.priority : b.priority - a.priority;
    }

    const aValue = (a[sortField] as string)?.toLowerCase() || '';
    const bValue = (b[sortField] as string)?.toLowerCase() || '';

    if (sortDirection === 'asc') {
      return aValue.localeCompare(bValue);
    } else {
      return bValue.localeCompare(aValue);
    }
  });

  const renderSortIndicator = (field: keyof AccessGroup) => {
    if (sortField !== field) return null;

    return sortDirection === 'asc' ? (
      <ChevronUp size={16} className="ml-1" />
    ) : (
      <ChevronDown size={16} className="ml-1" />
    );
  };

  const handleEditAccessGroup = (accessGroup: AccessGroup) => {
    setSelectedAccessGroup(accessGroup);
    setIsFormOpen(true);
  };

  const handleCreateAccessGroup = () => {
    setSelectedAccessGroup(null);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: CreateAccessGroupData | UpdateAccessGroupData) => {
    try {
      if (selectedAccessGroup) {
        await updateAccessGroup(data as UpdateAccessGroupData);
      } else {
        await createAccessGroup(data as CreateAccessGroupData);
      }
      await fetchAccessGroups();
      setIsFormOpen(false);
      setSelectedAccessGroup(null);
    } catch (error) {
      // Error handling is done in the hooks
    }
  };

  const accessGroupToDelete = deleteId
    ? accessGroups.find((group) => group.id === deleteId)?.name || 'this access group'
    : '';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header with title and button to add */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-foreground">
          Access Groups
        </h2>

        <button
          onClick={handleCreateAccessGroup}
          className="px-4 py-2 rounded-md transition-colors flex items-center bg-primary text-primary-foreground hover:opacity-90"
        >
          <Plus size={16} className="mr-2" />
          Add Access Group
        </button>
      </div>

      {/* Table of access groups */}
      <div
        className="overflow-x-auto rounded-lg shadow bg-background"
      >
        <table
          className="min-w-full divide-y bg-background"
        >
          <thead className="">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-primary"
              >
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Name {renderSortIndicator('name')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell text-primary"
              >
                <button
                  onClick={() => handleSort('description')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Description {renderSortIndicator('description')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell text-primary"
              >
                <button
                  onClick={() => handleSort('priority')}
                  className="flex items-center focus:outline-none text-primary"
                >
                  Priority {renderSortIndicator('priority')}
                </button>
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden xl:table-cell text-primary"
              >
                Roles
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-primary"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border w-full">
            {sortedAccessGroups.length === 0 ? (
              <tr className="w-full">
                <td colSpan={5} className="px-6 py-8 text-center w-full">
                  No access groups have been added yet. Use the "Add Access Group" button to create one.
                </td>
              </tr>
            ) : (
              sortedAccessGroups.map((accessGroup) => (
                <Fragment key={accessGroup.id}>
                  <tr className="hover:bg-muted/50 cursor-pointer transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary">
                            {accessGroup.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <div
                        className="text-sm truncate max-w-xs text-muted-foreground"
                      >
                        {accessGroup.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                      <span
                        className="px-2 py-1 text-xs rounded-full font-bold bg-primary text-primary-foreground"
                      >
                        {accessGroup.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden xl:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {accessGroup.roles.length === 0 ? (
                          <span
                            className="px-2 py-1 text-xs rounded-full bg-muted/60 text-muted-foreground font-medium"
                          >
                            No roles
                          </span>
                        ) : (
                          accessGroup.roles.slice(0, 2).map((roleId) => {
                            const role = roles.find(r => r.id === roleId);
                            return role ? (
                              <span
                                key={roleId}
                                className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary font-medium"
                              >
                                {role.name}
                              </span>
                            ) : null;
                          })
                        )}
                        {accessGroup.roles.length > 2 && (
                          <span
                            className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary font-medium"
                          >
                            +{accessGroup.roles.length - 2} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditAccessGroup(accessGroup)}
                        className="ml-4 transition-colors text-primary hover:opacity-70"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => showDeleteConfirmation(accessGroup.id)}
                        className="ml-4 transition-colors text-destructive hover:opacity-70"
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      <AccessGroupForm
        accessGroup={selectedAccessGroup || undefined}
        roles={roles}
        isOpen={isFormOpen}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setIsFormOpen(false);
          setSelectedAccessGroup(null);
        }}
        isSubmitting={createLoading || updateLoading}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleDeleteAccessGroup}
        groupName={accessGroupToDelete}
      />
    </div>
  );
};

export default AccessGroupsTable;
